import { Link } from 'react-router-dom';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';

export default function Hero() {
  const [showGuestModal, setShowGuestModal] = useState(false);
  const { t } = useTranslation('common');

  return (
    <>
      <section className="relative min-h-screen flex items-center justify-center overflow-hidden px-4 py-20">
        {/* Background */}
        <div className="absolute inset-0 bg-gradient-to-br from-gray-50 via-white to-teal-50"></div>
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_30%_50%,rgba(20,184,166,0.1),transparent_50%)]"></div>
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_70%_50%,rgba(20,184,166,0.05),transparent_50%)]"></div>

        <div className="relative max-w-7xl mx-auto w-full">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            {/* Left Content */}
            <div className="text-center lg:text-left">
              <h1 className="text-5xl md:text-6xl lg:text-7xl font-bold text-gray-900 mb-6 leading-tight">
                {t('hero.title')}
                <br />
                <span className="bg-gradient-to-r from-teal-600 to-teal-500 bg-clip-text text-transparent">
                  {t('hero.title2')}
                </span>
              </h1>
              <p className="text-xl md:text-2xl text-gray-600 mb-8 leading-relaxed">
                {t('hero.subtitle')}
                <br />
                {t('hero.subtitle2')}
              </p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center lg:justify-start">
                <button
                  onClick={() => setShowGuestModal(true)}
                  className="px-8 py-4 bg-gradient-to-r from-teal-500 to-teal-600 text-white rounded-lg font-bold text-lg hover:shadow-xl transition-all duration-300 whitespace-nowrap cursor-pointer"
                >
                  {t('hero.cta.primary')}
                </button>
                <Link
                  to="/how-it-works"
                  className="px-8 py-4 bg-white text-teal-600 rounded-lg font-bold text-lg hover:shadow-xl transition-all duration-300 whitespace-nowrap cursor-pointer"
                >
                  {t('hero.cta.secondary')}
                </Link>
              </div>
              <p className="text-sm text-gray-500 mt-6">
                {t('hero.noCreditCard')}
              </p>
            </div>

            {/* Right Content - Floating Cards */}
            <div className="relative h-96 lg:h-[500px]">
              {/* QR Code Card */}
              <div className="absolute top-0 right-0 w-64 bg-white rounded-2xl shadow-2xl p-6 animate-float">
                <div className="w-12 h-12 bg-gradient-to-br from-teal-500 to-teal-600 rounded-xl flex items-center justify-center mb-4">
                  <i className="ri-qr-code-line text-white text-2xl"></i>
                </div>
                <h3 className="text-lg font-bold text-gray-900 mb-2">{t('hero.card.qr.title')}</h3>
                <p className="text-sm text-gray-600">{t('hero.card.qr.desc')}</p>
                <div className="mt-4 h-32 bg-gray-100 rounded-lg flex items-center justify-center">
                  <i className="ri-qr-code-line text-gray-400 text-6xl"></i>
                </div>
              </div>

              {/* Short Link Card */}
              <div className="absolute bottom-20 left-0 w-64 bg-white rounded-2xl shadow-2xl p-6 animate-float-delayed">
                <div className="w-12 h-12 bg-gradient-to-br from-teal-500 to-teal-600 rounded-xl flex items-center justify-center mb-4">
                  <i className="ri-link text-white text-2xl"></i>
                </div>
                <h3 className="text-lg font-bold text-gray-900 mb-2">{t('hero.card.short.title')}</h3>
                <p className="text-sm text-gray-600">{t('hero.card.short.desc')}</p>
                <div className="mt-4 space-y-2">
                  <div className="h-3 bg-gray-100 rounded"></div>
                  <div className="h-3 bg-teal-100 rounded w-3/4"></div>
                </div>
              </div>

              {/* Bio Link Card */}
              <div className="absolute bottom-0 right-12 w-64 bg-white rounded-2xl shadow-2xl p-6 animate-float">
                <div className="w-12 h-12 bg-gradient-to-br from-teal-500 to-teal-600 rounded-xl flex items-center justify-center mb-4">
                  <i className="ri-user-line text-white text-2xl"></i>
                </div>
                <h3 className="text-lg font-bold text-gray-900 mb-2">{t('hero.card.bio.title')}</h3>
                <p className="text-sm text-gray-600">{t('hero.card.bio.desc')}</p>
                <div className="mt-4 space-y-2">
                  <div className="h-8 bg-gray-100 rounded-lg"></div>
                  <div className="h-8 bg-gray-100 rounded-lg"></div>
                  <div className="h-8 bg-gray-100 rounded-lg"></div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Guest Mode Modal */}
      {showGuestModal && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl shadow-2xl max-w-md w-full p-8 animate-scale-in">
            <div className="text-center mb-6">
              <div className="w-16 h-16 bg-gradient-to-br from-teal-500 to-teal-600 rounded-2xl flex items-center justify-center mx-auto mb-4">
                <i className="ri-gift-line text-white text-3xl"></i>
              </div>
              <h3 className="text-2xl font-bold text-gray-900 mb-2">{t('guest.modal.title')}</h3>
              <p className="text-gray-600">
                {t('guest.modal.subtitle')}
              </p>
            </div>

            <div className="space-y-3 mb-6">
              <div className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg">
                <i className="ri-qr-code-line text-teal-600 text-xl"></i>
                <span className="text-gray-700">{t('guest.modal.qr')} 3</span>
              </div>
              <div className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg">
                <i className="ri-link text-teal-600 text-xl"></i>
                <span className="text-gray-700">{t('guest.modal.short')} 3</span>
              </div>
              <div className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg">
                <i className="ri-user-line text-teal-600 text-xl"></i>
                <span className="text-gray-700">{t('guest.modal.bio')} 3</span>
              </div>
            </div>

            <div className="bg-teal-50 border border-teal-200 rounded-lg p-4 mb-6">
              <p className="text-sm text-teal-800">
                <strong>{t('guest.modal.tip')}</strong> {t('guest.modal.tipText')}
              </p>
            </div>

            <div className="flex gap-3">
              <button
                onClick={() => setShowGuestModal(false)}
                className="flex-1 px-6 py-3 bg-gray-100 text-gray-700 rounded-lg font-medium hover:bg-gray-200 transition-colors whitespace-nowrap cursor-pointer"
              >
                {t('guest.modal.cancel')}
              </button>
              <Link
                to="/dashboard"
                className="flex-1 px-6 py-3 bg-gradient-to-r from-teal-500 to-teal-600 text-white rounded-lg font-medium hover:shadow-lg transition-all text-center whitespace-nowrap cursor-pointer"
              >
                {t('guest.modal.start')}
              </Link>
            </div>
          </div>
        </div>
      )}

      <style>{`
        @keyframes float {
          0%, 100% { transform: translateY(0px); }
          50% { transform: translateY(-20px); }
        }
        @keyframes float-delayed {
          0%, 100% { transform: translateY(0px); }
          50% { transform: translateY(-15px); }
        }
        @keyframes scale-in {
          0% { transform: scale(0.9); opacity: 0; }
          100% { transform: scale(1); opacity: 1; }
        }
        .animate-float {
          animation: float 6s ease-in-out infinite;
        }
        .animate-float-delayed {
          animation: float-delayed 6s ease-in-out infinite 1s;
        }
        .animate-scale-in {
          animation: scale-in 0.3s ease-out;
        }
      `}</style>
    </>
  );
}
